import { Box } from '@mui/material';
import { PustyWykres } from '@/components/wykresy/Wykresy';
import { osRoznicuje, rangi } from '@/lib/rangi';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * PORTFOLIO — mapa bąbelkowa w czterech ćwiartkach (warstwa wspólna nr 7)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * KONTRAKT WEJŚCIA JEST JAWNY I ZAMKNIĘTY: X, Y, ROZMIAR, BYT, ŹRÓDŁO. Komponent NIE domyśla się,
 * co znaczą liczby, z etykiety osi ani z nazwy pola. Wykres, który sam nadaje sens danym,
 * prędzej czy później nada im zły.
 *
 * BAŃKI RYSUJEMY WYŁĄCZNIE Z KOMPLETNYCH WSPÓŁRZĘDNYCH. Punkt, w którym brakuje którejkolwiek
 * z trzech wielkości, NIE JEST rysowany — i jest policzony w podpisie. Domyślne zero zamiast
 * brakującej wartości wrzuciłoby byt w lewy dolny róg, czyli w ćwiartkę „najsłabsze", i wyglądałby
 * tam dokładnie tak samo jak byt zmierzony. To nie jest usterka wyglądu, tylko fałszywy wniosek
 * postawiony przez wykres.
 *
 * PRZY BRAKU DANYCH: rysujemy RAMĘ Z ĆWIARTKAMI i uczciwy komunikat wewnątrz niej. Rama zostaje,
 * bo pokazuje, o jakie pytanie chodzi; treść mówi, dlaczego nie ma odpowiedzi.
 */

export interface PunktPortfolio {
  /** Identyfikator bytu — nie etykieta. Etykieta bywa niejednoznaczna, identyfikator nie. */
  id: string;
  nazwa: string;
  x: number | null;
  y: number | null;
  rozmiar: number | null;
  /** Indeks barwy serii (0–6). Ten sam byt ma mieć ten sam kolor na każdym ekranie. */
  seria?: number;
}

export interface OsiePortfolio {
  x: { etykieta: string; niska: string; wysoka: string };
  y: { etykieta: string; niska: string; wysoka: string };
  rozmiar: string;
}

export function Portfolio({
  punkty,
  osie,
  zrodlo,
  komunikatPusty = 'Brak danych, żeby umieścić byty na mapie. Rama pokazuje, o jakie pytanie chodzi.',
  wysokosc = 460,
  minProba = 5,
}: {
  punkty: readonly PunktPortfolio[] | null | undefined;
  osie: OsiePortfolio;
  /** Skąd są liczby — stoi przy wykresie, nie w przypisie. */
  zrodlo: string;
  komunikatPusty?: string;
  wysokosc?: number;
  /**
   * Najmniejsza liczba KOMPLETNYCH bytów, przy której wolno narysować ćwiartki.
   *
   * ĆWIARTKI SĄ WZGLĘDNE WOBEC ZBIORU, nie wobec żadnego progu z zewnątrz — a przy trzech
   * pozycjach „powyżej mediany" znaczy dokładnie tyle co „druga z trzech". Mapa narysowana
   * z takiej próby wygląda jak wniosek i nim nie jest.
   *
   * Tu jest to WAŻNIEJSZE niż w makiecie: makieta stawia bańki wprost z wartości, a produkt
   * z RANGI. Ranga rozkłada byty równomiernie NIEZALEŻNIE od tego, jak blisko siebie leżą —
   * więc trzy prawie identyczne dania dostałyby ładnie rozrzucone ćwiartki i wyglądały na
   * cztery różne strategie produktowe.
   *
   * Wartość 5 przeniesiona z makiety (`PROG_PROBY`, js/screens/dania.js).
   */
  minProba?: number;
}) {
  const wszystkie = punkty ?? [];
  const kompletne = wszystkie.filter(
    (p) => p.x !== null && p.y !== null && p.rozmiar !== null && Number.isFinite(p.x) && Number.isFinite(p.y),
  );
  const pominiete = wszystkie.length - kompletne.length;

  const maxR = Math.max(...kompletne.map((p) => p.rozmiar as number), 1);

  /*
    POZYCJA Z RANGI, NIE Z SUROWEJ WARTOŚCI — tak samo jak w makiecie (js/screens/dania.js:405).

    Pierwsza wersja mapowała wartość wprost na procent i wywróciła się przy pierwszym realnym
    zbiorze: wszystkie dania miały sprzedaż w tych samych 5 szkołach, więc każde `x/maxX`
    wyszło 1.0, bańki skleiły się w słupek przy prawej krawędzi i przykryły etykietę ćwiartki.
    Zbiory, w których jedna wielkość się powtarza, są w tym produkcie regułą, nie wyjątkiem.

    Ranga rozkłada byty równomiernie i jest odporna na wartości odstające. Cena, którą trzeba
    zapłacić uczciwie: odległość na osi przestaje znaczyć „o tyle więcej" i zaczyna znaczyć
    „o tyle miejsc dalej". Dlatego podpis pod wykresem mówi to WPROST — inaczej ten sam obraz
    byłby po prostu fałszywy.
  */
  const wartosciX = kompletne.map((p) => p.x as number);
  const wartosciY = kompletne.map((p) => p.y as number);
  const rangaX = rangi(wartosciX);
  const rangaY = rangi(wartosciY);

  /* Oś, na której wszystkie byty mają tę samą wartość, daje jedną kolumnę pośrodku. To poprawny
     obraz, ale bez podpisu czyta się jak „wszystko przeciętne" albo jak zepsuty wykres. */
  const nieroznicujace = [
    osRoznicuje(wartosciX) ? null : `poziomej (${osie.x.etykieta})`,
    osRoznicuje(wartosciY) ? null : `pionowej (${osie.y.etykieta})`,
  ].filter((x): x is string => x !== null);

  return (
    <Box>
      <Box
        className="portfolio"
        sx={{ height: wysokosc }}
        role="img"
        aria-label={
          kompletne.length === 0
            ? `Mapa portfolio: ${osie.x.etykieta} wobec ${osie.y.etykieta}. Brak bytów do umieszczenia.`
            : `Mapa portfolio: ${osie.x.etykieta} (poziomo) wobec ${osie.y.etykieta} (pionowo), wielkość bańki to ${osie.rozmiar}. ` +
              `${kompletne.length} bytów: ${kompletne.map((p) => p.nazwa).join(', ')}.`
        }
      >
        {/* CZTERY etykiety ćwiartek, tak jak w makiecie. Produkt rysował tylko dwie — po
            przekątnej — więc ćwiartki MIESZANE („szeroko, ale rzadko" i „wąsko, ale mocno")
            były bezimienne, a to właśnie one niosą decyzję. Mapa czytała się jak oś, nie jak
            podział. Nazwy składamy z opisów osi, żeby nie istniała druga lista słów do
            utrzymania obok tej, którą ekran już podał. */}
        {kompletne.length >= minProba ? (
          <>
            <Box className="kwadrant-etykieta" sx={{ top: 10, left: 14 }}>
              {`${osie.x.niska} · ${osie.y.wysoka}`}
            </Box>
            <Box className="kwadrant-etykieta" sx={{ top: 10, right: 14 }}>
              {`${osie.x.wysoka} · ${osie.y.wysoka}`}
            </Box>
            <Box className="kwadrant-etykieta" sx={{ bottom: 10, left: 14 }}>
              {`${osie.x.niska} · ${osie.y.niska}`}
            </Box>
            <Box className="kwadrant-etykieta" sx={{ bottom: 10, right: 14 }}>
              {`${osie.x.wysoka} · ${osie.y.niska}`}
            </Box>
          </>
        ) : null}

        {kompletne.length === 0 ? (
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', p: 3 }}>
            <PustyWykres wysokosc={0} tytul="Nie ma czego umieścić na mapie" komunikat={komunikatPusty} />
          </Box>
        ) : kompletne.length < minProba ? (
          /* PRÓBA ZA MAŁA NA ĆWIARTKI — rama zostaje, bańki nie. To nie jest brak danych:
             dane są, tylko nie wystarczą na TEN podział. Dwie różne odpowiedzi, dwa różne teksty. */
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', p: 3 }}>
            <PustyWykres
              wysokosc={0}
              tytul="Za mało bytów, żeby rysować ćwiartki"
              komunikat={
                `Podział na ćwiartki jest WZGLĘDNY wobec zbioru: przy ${kompletne.length} ` +
                `${kompletne.length === 1 ? 'pozycji' : 'pozycjach'} „powyżej mediany" znaczy tyle co ` +
                `„druga z ${kompletne.length}". Wymagane minimum: ${minProba}. Liczby są prawdziwe — ` +
                'nie wystarczają na ten konkretny obraz.'
              }
            />
          </Box>
        ) : (
          kompletne.map((p, i) => {
            /* Średnica z PIERWIASTKA udziału: proporcjonalne ma być POLE bańki, nie średnica.
               Skalowanie średnicą zawyża duże pozycje do kwadratu — to najczęstsze kłamstwo
               wykresów bąbelkowych. Piksele są tu DANĄ, nie odstępem projektowym. */
            const srednica = 24 + 44 * Math.sqrt((p.rozmiar as number) / maxR);
            return (
              <Box
                key={p.id}
                className="banka"
                data-nazwa={p.nazwa}
                title={`${p.nazwa} — ${osie.x.etykieta}: ${p.x}, ${osie.y.etykieta}: ${p.y}`}
                sx={{
                  /* 10–90%: największa bańka ma się zmieścić w siatce ćwiartek. Bańka
                     wychodząca poza ramę sugerowałaby, że skala się urwała. */
                  left: `${(10 + rangaX[i]! * 80).toFixed(1)}%`,
                  top: `${(90 - rangaY[i]! * 80).toFixed(1)}%`,
                  width: srednica,
                  height: srednica,
                  background: `var(--seria-${((p.seria ?? 0) % 7) + 1})`,
                }}
              />
            );
          })
        )}
      </Box>

      <Box sx={{ mt: 1.5, fontSize: 'var(--st-drobny)', color: 'var(--tekst-slaby)' }}>
        {`Poziomo: ${osie.x.etykieta} · pionowo: ${osie.y.etykieta} · wielkość: ${osie.rozmiar} · źródło: ${zrodlo}`}
        {/* Bez tego zdania wykres sugerowałby, że odległości są proporcjonalne do wartości.
            Gdy mapy nie ma, zdanie znika — opis własności rysunku, którego nie narysowano,
            jest twierdzeniem o niczym. */}
        {kompletne.length >= minProba ? (
          <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
            Osie pokazują POZYCJĘ W ZESTAWIENIU, nie wartość bezwzględną — sąsiedztwo dwóch baniek
            znaczy „obok siebie w kolejności", a nie „o tyle samo więcej".
          </Box>
        ) : null}
        {nieroznicujace.length > 0 ? (
          <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'var(--uwaga)' }}>
            {`W tym oknie wszystkie byty mają identyczną wartość na osi ${nieroznicujace.join(' i ')} — ta oś ich nie rozróżnia. Ustawienie w jednej linii jest wynikiem pomiaru, nie usterką wykresu.`}
          </Box>
        ) : null}
        {pominiete > 0 ? (
          /* Pominięte byty MUSZĄ być policzone na wierzchu. Cicha nieobecność na mapie czyta się
             jak „tego nie ma", a prawdą jest „nie wiemy, gdzie to postawić". */
          <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'var(--uwaga)' }}>
            {`${pominiete} bytów nie ma kompletu współrzędnych i NIE zostało narysowanych — brak wartości to nie jest zero.`}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
